import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Calendar, Users, DollarSign, Settings, 
  LogOut, Check, X, Clock, Eye 
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/admin/login`, { password });
      if (res.data.success) {
        localStorage.setItem("admin_auth", "true");
        onLogin();
        toast.success("Welcome back!");
      }
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl border border-brand-border w-full max-w-md"
      >
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl mb-2">Admin Login</h1>
          <p className="text-muted-foreground">Weaver's Nest Cottage</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2"
              placeholder="Enter admin password"
              required
              data-testid="admin-password"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-primary w-full"
            data-testid="admin-login-btn"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
        
        <p className="text-xs text-center text-muted-foreground mt-6">
          Default password: weaversnest2024
        </p>
      </motion.div>
    </div>
  );
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("bookings");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [pricing, setPricing] = useState({
    base_price: 2500,
    cleaning_fee: 500,
    min_nights: 2
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dashboard`);
      setDashboardData(res.data);
      
      const availRes = await axios.get(`${API_URL}/api/availability`);
      setPricing(availRes.data.pricing);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Booking ${newStatus}`);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handlePricingUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/availability/pricing`, pricing);
      toast.success("Pricing updated!");
    } catch (error) {
      toast.error("Failed to update pricing");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_auth");
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Header */}
      <header className="bg-white border-b border-brand-border sticky top-20 z-30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <h1 className="font-heading text-xl">Admin Dashboard</h1>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-muted-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      
      {/* Stats */}
      <section className="py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-xl border border-brand-border">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-brand-sage" />
                <span className="text-muted-foreground text-sm">Total Bookings</span>
              </div>
              <p className="font-heading text-3xl">{stats.total_bookings || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-brand-border">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-muted-foreground text-sm">Pending</span>
              </div>
              <p className="font-heading text-3xl">{stats.pending_bookings || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-brand-border">
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-muted-foreground text-sm">Confirmed</span>
              </div>
              <p className="font-heading text-3xl">{stats.confirmed_bookings || 0}</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tabs */}
      <section className="pb-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex gap-2 mb-6">
            {[
              { id: "bookings", label: "Bookings", icon: Calendar },
              { id: "pricing", label: "Pricing", icon: DollarSign },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-brand-sage text-white"
                    : "bg-white text-brand-charcoal hover:bg-brand-muted"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <div className="bg-white rounded-xl border border-brand-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-cream">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Guest</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Dates</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {dashboardData?.bookings?.map((booking) => (
                      <tr key={booking.id} className="hover:bg-brand-cream/50">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium">{booking.guest_name}</p>
                            <p className="text-sm text-muted-foreground">{booking.guest_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm">
                          {booking.check_in} → {booking.check_out}
                        </td>
                        <td className="px-4 py-4 font-medium">
                          R {booking.total_amount?.toLocaleString()}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === "confirmed" 
                              ? "bg-green-100 text-green-700"
                              : booking.status === "cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          {booking.is_bridal && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                              Bridal
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedBooking(booking)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {booking.status === "pending" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-600"
                                  onClick={() => handleStatusUpdate(booking.id, "confirmed")}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600"
                                  onClick={() => handleStatusUpdate(booking.id, "cancelled")}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!dashboardData?.bookings || dashboardData.bookings.length === 0) && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                          No bookings yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div className="bg-white rounded-xl border border-brand-border p-6 max-w-lg">
              <h2 className="font-heading text-xl mb-6">Pricing Settings</h2>
              <form onSubmit={handlePricingUpdate} className="space-y-6">
                <div>
                  <Label htmlFor="base_price">Nightly Rate (ZAR)</Label>
                  <Input
                    id="base_price"
                    type="number"
                    value={pricing.base_price}
                    onChange={(e) => setPricing({...pricing, base_price: parseFloat(e.target.value)})}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="cleaning_fee">Cleaning Fee (ZAR)</Label>
                  <Input
                    id="cleaning_fee"
                    type="number"
                    value={pricing.cleaning_fee}
                    onChange={(e) => setPricing({...pricing, cleaning_fee: parseFloat(e.target.value)})}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="min_nights">Minimum Nights</Label>
                  <Input
                    id="min_nights"
                    type="number"
                    value={pricing.min_nights}
                    onChange={(e) => setPricing({...pricing, min_nights: parseInt(e.target.value)})}
                    className="mt-2"
                  />
                </div>
                <Button type="submit" className="btn-primary">
                  Save Pricing
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>
      
      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Guest Name</p>
                  <p className="font-medium">{selectedBooking.guest_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedBooking.guest_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedBooking.guest_phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Guests</p>
                  <p className="font-medium">{selectedBooking.guests}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Check-in</p>
                  <p className="font-medium">{selectedBooking.check_in}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Check-out</p>
                  <p className="font-medium">{selectedBooking.check_out}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total</p>
                  <p className="font-medium text-brand-sage">
                    R {selectedBooking.total_amount?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Bridal Booking</p>
                  <p className="font-medium">{selectedBooking.is_bridal ? "Yes" : "No"}</p>
                </div>
              </div>
              {selectedBooking.special_requests && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Special Requests</p>
                  <p className="bg-brand-cream p-3 rounded-lg text-sm">
                    {selectedBooking.special_requests}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem("admin_auth");
    setIsAuthenticated(auth === "true");
  }, []);

  if (!isAuthenticated) {
    return <LoginForm onLogin={() => setIsAuthenticated(true)} />;
  }

  return <Dashboard />;
}
